import React, { useState, useEffect } from "react";
import {
  setPassword,
  changePassword,
  getCurrentUser,
  updateProfile,
} from "../services/api";
import Layout from "../components/Layout";

const AccountSettings: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [hasPassword, setHasPassword] = useState(false);

  // Profile fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getCurrentUser();
        setHasPassword(userData.hasPassword);
        setFirstName(userData.firstName || "");
        setLastName(userData.lastName || "");
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setMessage("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      await setPassword(newPassword);
      setMessage("Password set successfully!");
      setHasPassword(true);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Error setting password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setMessage("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      await changePassword(currentPassword, newPassword);
      setMessage("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Error changing password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      setProfileMessage("First name and last name are required");
      return;
    }

    setProfileLoading(true);
    setProfileMessage("");

    try {
      await updateProfile(firstName.trim(), lastName.trim());
      setProfileMessage("Profile updated successfully!");
    } catch (error: any) {
      setProfileMessage(
        error.response?.data?.message || "Error updating profile"
      );
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <Layout showBackButton={true}>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h1 className="text-2xl font-bold text-neutral-900">
              Ustawienia konta
            </h1>
            <p className="mt-1 text-neutral-600">
              Zarządzaj informacjami o swoim profilu, hasłem i ustawieniami
              bezpieczeństwa
            </p>
          </div>

          <div className="p-6">
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">
                Informacje o profilu
              </h2>
              <p className="text-sm text-neutral-600 mb-6">
                Zaktualizuj swoje dane osobowe
              </p>

              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    Imię
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Wprowadź swoje imię"
                  />
                </div>

                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    Nazwisko
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Wprowadź swoje nazwisko"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={profileLoading}
                    className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50"
                  >
                    {profileLoading ? "Zapisywanie..." : "Zaktualizuj profil"}
                  </button>
                </div>

                {profileMessage && (
                  <div
                    className={`text-sm font-medium ${
                      profileMessage.includes("successfully")
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    {profileMessage}
                  </div>
                )}
              </form>
            </div>

            <div className="border-t border-neutral-200 pt-6 mb-8">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">
                {hasPassword ? "Zmień hasło" : "Ustaw hasło"}
              </h2>
              <p className="text-sm text-neutral-600 mb-6">
                {hasPassword
                  ? "Zaktualizuj swoje obecne hasło na nowe"
                  : "Ustaw hasło, aby umożliwić logowanie za pomocą hasła oprócz magicznego linku"}
              </p>

              <form
                onSubmit={
                  hasPassword ? handleChangePassword : handleSetPassword
                }
                className="space-y-4"
              >
                {hasPassword && (
                  <div>
                    <label
                      htmlFor="currentPassword"
                      className="block text-sm font-medium text-neutral-700 mb-2"
                    >
                      Obecne hasło
                    </label>
                    <input
                      id="currentPassword"
                      name="currentPassword"
                      type="password"
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Wprowadź swoje obecne hasło"
                    />
                  </div>
                )}

                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    {hasPassword ? "Nowe hasło" : "Hasło"}
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Wprowadź swoje nowe hasło"
                    minLength={8}
                  />
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    Potwierdź hasło
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Potwierdź swoje nowe hasło"
                    minLength={8}
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50"
                  >
                    {isLoading
                      ? "Zapisywanie..."
                      : hasPassword
                        ? "Zmień hasło"
                        : "Ustaw hasło"}
                  </button>
                </div>

                {message && (
                  <div
                    className={`text-sm font-medium ${
                      message.includes("successfully")
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    {message}
                  </div>
                )}
              </form>
            </div>

            <div className="border-t border-neutral-200 pt-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Metody logowania
              </h3>
              <div className="space-y-2 text-sm text-neutral-600">
                <div className="flex items-center">
                  <svg
                    className="h-4 w-4 text-success-600 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Magiczny link (Email)
                </div>
                <div className="flex items-center">
                  <svg
                    className={`h-4 w-4 mr-2 ${hasPassword ? "text-success-600" : "text-neutral-400"}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    {hasPassword ? (
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    ) : (
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    )}
                  </svg>
                  Hasło {hasPassword ? "(Włączone)" : "(Nieustawione)"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AccountSettings;
